// Category Trending domain logic.
// Given a list of ChartBrowserEntry items, compute how many apps fall into
// each genre/category and surface the breakdown as a sorted list.
// Pure function, no side effects.

/**
 * @typedef {object} CategoryBreakdown
 * @property {string} genre - Genre name (e.g. "Games").
 * @property {string|null} genreId - Apple genre ID (e.g. "6014"), or null if unknown.
 * @property {number} count - Number of apps in this genre.
 * @property {number} pct - Percentage (0–100), rounded to 1 decimal.
 */

/**
 * @typedef {object} CategoryConcentrationSummary
 * @property {string|null} topGenre - Dominant genre name, or null for empty input.
 * @property {number} topCount - Number of apps in the dominant genre.
 * @property {number} topPct - Dominant genre percentage (0–100), rounded to 1 decimal.
 * @property {number} distinctGenres - Number of genres represented.
 */

export const ALL_GENRES = 'all';

/**
 * @param {unknown} genre
 * @returns {string}
 */
function normalizedGenre(genre) {
  return String(genre ?? '').trim() || 'Unknown';
}

/**
 * Compute the genre breakdown of a list of chart entries.
 * Returns genres sorted by count descending, then alphabetically.
 *
 * @param {Array<{primaryGenre?: string|null, genreId?: string|null}>} entries
 * @returns {CategoryBreakdown[]}
 */
export function computeCategoryBreakdown(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return [];

  /** @type {Map<string, {genreId: string|null, count: number}>} */
  const map = new Map();

  for (const entry of entries) {
    const genre = normalizedGenre(entry.primaryGenre);
    const gid = entry.genreId ?? null;
    const existing = map.get(genre);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(genre, { genreId: gid, count: 1 });
    }
  }

  const total = entries.length;
  return Array.from(map.entries())
    .map(([genre, { genreId, count }]) => ({
      genre,
      genreId,
      count,
      pct: Math.round((count / total) * 1000) / 10, // 1 decimal place
    }))
    .sort((a, b) => b.count - a.count || a.genre.localeCompare(b.genre));
}

/**
 * Return entries whose normalized primary genre matches the selected genre.
 * The "all" sentinel returns a shallow copy of the full input list.
 *
 * @param {Array<{primaryGenre?: string|null}>} entries
 * @param {string|null|undefined} genre
 * @returns {Array<{primaryGenre?: string|null}>}
 */
export function filterEntriesByGenre(entries, genre) {
  if (!Array.isArray(entries)) return [];

  const selected = String(genre ?? '').trim();
  if (!selected || selected === ALL_GENRES) return [...entries];

  return entries.filter((entry) => normalizedGenre(entry.primaryGenre) === selected);
}

/**
 * Summarize a category breakdown into a one-line concentration insight.
 * Ties are resolved by genre name so the result stays deterministic even when
 * callers pass an unsorted breakdown.
 *
 * @param {CategoryBreakdown[]} breakdown
 * @returns {CategoryConcentrationSummary}
 */
export function summarizeCategoryConcentration(breakdown) {
  if (!Array.isArray(breakdown) || breakdown.length === 0) {
    return { topGenre: null, topCount: 0, topPct: 0, distinctGenres: 0 };
  }

  const sorted = [...breakdown].sort((a, b) => b.count - a.count || a.genre.localeCompare(b.genre));
  const top = sorted[0];

  return {
    topGenre: top.genre,
    topCount: top.count,
    topPct: top.pct,
    distinctGenres: breakdown.length,
  };
}
